import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Plot from "react-plotly.js";
import "../styles/PagesModern.css";
import CategoryIcon from "./CategoryIcon";
import { LoadingOverlay, PageHeader, PanelHeader, SegmentedControl } from "./RefreshUI";
import { CHART, chartAxis, chartBase, chartWatermark, panelChartWidth } from "../utils/chartTheme";

const IRatingGraph = () => {
        const [iRatings, setIRatings] = useState([]);
        const [loading, setLoading] = useState(true);
        const [userIRating, setUserIRating] = useState("");
        const [percentile, setPercentile] = useState(null);
        const [dataType, setDataType] = useState("SportsCar");
        const [timeFrame, setTimeFrame] = useState("All Time");
        const [startRange, setStartRange] = useState("All");
        const [licenseClass, setLicenseClass] = useState("All");
        const debounceTimer = useRef(null);

        useEffect(() => {
                setUserIRating("");
                setPercentile(null);
                setIRatings([]);
                setLoading(true);

                // Defer fetch to next tick to ensure loading state renders first
                const timer = setTimeout(() => {
                        async function fetchData() {
                                try {
                                        const timeFrameValue = getTimeFrameValue(timeFrame);
                                        const rangeValue = getStartRangeValue(startRange);
                                        const licenseValue = getLicenseClassValue(licenseClass);

                                        let url;
                                        if (licenseValue) {
                                                // License filter is selected - use new combined endpoints
                                                if (rangeValue) {
                                                        // Race count filter is also set
                                                        if (timeFrameValue === 1) {
                                                                // License + starts only (All Time)
                                                                url = `https://iracing6-backend.herokuapp.com/api/irating-data/iratings${dataType}ByLicenseAndStarts/${licenseValue}/${rangeValue}`;
                                                        } else {
                                                                // License + year + starts
                                                                url = `https://iracing6-backend.herokuapp.com/api/irating-data/iratings${dataType}ByLicense/${licenseValue}/${timeFrameValue}/${rangeValue}`;
                                                        }
                                                } else {
                                                        // No race count filter
                                                        if (timeFrameValue === 1) {
                                                                // License only (All Time)
                                                                url = `https://iracing6-backend.herokuapp.com/api/irating-data/iratings${dataType}ByLicense/${licenseValue}`;
                                                        } else {
                                                                // License + year
                                                                url = `https://iracing6-backend.herokuapp.com/api/irating-data/iratings${dataType}ByLicense/${licenseValue}/${timeFrameValue}`;
                                                        }
                                                }
                                        } else {
                                                // No license filter (All licenses) - use old time-based endpoint
                                                url = `https://iracing6-backend.herokuapp.com/api/irating-data/iratings${dataType}/${timeFrameValue}`;
                                                if (rangeValue) {
                                                        url += `/${rangeValue}`;
                                                }
                                        }

                                        const response = await axios.get(url);

                                        setIRatings(response.data);
                                        setLoading(false);
                                } catch (error) {
                                        console.error("Error fetching iRatings data:", error);
                                        setLoading(false);
                                }
                        }
                        fetchData();
                }, 0);

                return () => clearTimeout(timer);
        }, [dataType, timeFrame, startRange, licenseClass]);

        // Cleanup debounce timer on unmount
        useEffect(() => {
                return () => {
                        if (debounceTimer.current) {
                                clearTimeout(debounceTimer.current);
                        }
                };
        }, []);

        const getTimeFrameValue = (timeFrame) => {
                switch (timeFrame) {
                        case "1 Year":
                                return 2024;
                        case "2 Years":
                                return 2023;
                        case "3 Years":
                                return 2022;
                        case "4 Years":
                                return 2021;
                        case "All Time":
                                return 1;
                        default:
                                return 1;
                }
        };

        const getStartRangeValue = (range) => {
                switch (range) {
                        case "All":
                                return null;
                        case "5-50":
                                return "5-50";
                        case "51-200":
                                return "51-200";
                        case "201-500":
                                return "201-500";
                        case "501+":
                                return "501+";
                        default:
                                return null;
                }
        };

        const getLicenseClassValue = (licenseClass) => {
                switch (licenseClass) {
                        case "All":
                                return null;
                        case "Rookie":
                                return "rookie";
                        case "Class D":
                                return "class_d";
                        case "Class C":
                                return "class_c";
                        case "Class B":
                                return "class_b";
                        case "Class A":
                                return "class_a";
                        case "Pro":
                                return "pro";
                        default:
                                return null;
                }
        };

        const calculatePercentile = (iRating) => {
                const sortedIRatings = [...iRatings].sort((a, b) => a - b);
                const index = sortedIRatings.findIndex((r) => r > iRating);
                const percentile = index === -1 ? 100 : (index / sortedIRatings.length) * 100;
                setPercentile(percentile);
        };

        const handleUserIRatingChange = (event) => {
                const value = event.target.value;
                setUserIRating(value);

                // Clear existing timeout
                if (debounceTimer.current) {
                        clearTimeout(debounceTimer.current);
                }

                // Set new timeout to calculate percentile after delay
                if (value !== "") {
                        debounceTimer.current = setTimeout(() => {
                                calculatePercentile(parseInt(value, 10));
                        }, 500);
                } else {
                        setPercentile(null);
                }
        };

        const groupIRatingsByRange = (iRatings) => {
                const groupedIRatings = {};

                iRatings.forEach((iRating) => {
                        const range = Math.floor(iRating / 100) * 100;
                        if (groupedIRatings[range]) {
                                groupedIRatings[range] += 1;
                        } else {
                                groupedIRatings[range] = 1;
                        }
                });

                return groupedIRatings;
        };

        const hasData = !loading && iRatings.length > 0;
        const groupedIRatings = groupIRatingsByRange(iRatings);
        const rangeKeys = Object.keys(groupedIRatings).map(Number);
        const maxRange = rangeKeys.length ? Math.max(...rangeKeys) : 0;
        const minRange = rangeKeys.length ? Math.min(...rangeKeys) : 0;

        // Mean and median
        const mean = hasData ? iRatings.reduce((sum, rating) => sum + rating, 0) / iRatings.length : 0;
        const sortedIRatings = [...iRatings].sort((a, b) => a - b);
        const median = !hasData
                ? 0
                : sortedIRatings.length % 2 === 0
                  ? (sortedIRatings[sortedIRatings.length / 2 - 1] + sortedIRatings[sortedIRatings.length / 2]) / 2
                  : sortedIRatings[Math.floor(sortedIRatings.length / 2)];

        const totalResults = iRatings.length;
        const percentageOfResults = Object.values(groupedIRatings).map((count) => (count / totalResults) * 100);
        const isWide = window.innerWidth > 800;

        const data = [
                {
                        x: rangeKeys.map((range) => range + 50),
                        y: Object.values(groupedIRatings),
                        type: "bar",
                        marker: { color: CHART.blue },
                        hovertemplate: "%{x:.0f} iR: %{customdata:.2f}% of drivers<extra></extra>",
                        customdata: percentageOfResults,
                },
        ];

        const layout = {
                ...chartBase(),
                bargap: 0.08,
                xaxis: {
                        ...chartAxis(),
                        title: { text: "iRating", font: { size: 12, color: CHART.muted }, standoff: 8 },
                        tick0: 0,
                        dtick: isWide ? 500 : 1000,
                        showgrid: false,
                        range: licenseClass === "Pro" ? [Math.max(0, minRange - 1000), maxRange + 1000] : isWide ? [0, 5000] : [0, 3000],
                },
                yaxis: { ...chartAxis(), title: { text: "Drivers", font: { size: 12, color: CHART.muted } }, tickformat: "~s", dtick: licenseClass === "Pro" ? 1 : undefined },
                annotations: [chartWatermark("top-right")],
                width: panelChartWidth(),
                height: isWide ? 520 : 420,
                margin: isWide ? { l: 64, r: 16, t: 8, b: 48 } : { l: 44, r: 8, t: 8, b: 44 },
                autosize: false,
        };

        const CATEGORY_OPTIONS = [
                { value: "SportsCar", title: "Sports car", content: <CategoryIcon category="sports_car" size={16} /> },
                { value: "FormulaCar", title: "Formula car", content: <CategoryIcon category="formula_car" size={16} /> },
                { value: "Oval", title: "Oval", content: <CategoryIcon category="oval" size={16} /> },
                { value: "DirtRoad", title: "Dirt road", content: <CategoryIcon category="dirt_road" size={16} /> },
                { value: "DirtOval", title: "Dirt oval", content: <CategoryIcon category="dirt_oval" size={16} /> },
                { value: "Road", title: "Road (old)", content: "Road (old)" },
        ];
        const categoryTitle = CATEGORY_OPTIONS.find((opt) => opt.value === dataType)?.title || dataType;
        const filterSummary = [
                timeFrame === "All Time" ? "all time" : `last ${timeFrame.toLowerCase()}`,
                startRange === "All" ? "1+ races" : `${startRange} races`,
                licenseClass === "All" ? "all licenses" : licenseClass,
        ].join(", ");

        const changeCategory = (value) => {
                setDataType(value);
                setUserIRating("");
                setPercentile(null);
                setLicenseClass("All");
        };

        return (
                <div className="pg">
                        <main className="hp-main">
                                <PageHeader title="iRating distribution" lede="How iRatings are spread across drivers in each category. Filter by when drivers last raced, how much they race, and license." />

                                <section className="hp-panel pg-filters" aria-label="Filters">
                                        <div className="pg-field">
                                                <span className="pg-field__label">Category</span>
                                                <SegmentedControl label="Category" value={dataType} onChange={changeCategory} options={CATEGORY_OPTIONS} />
                                        </div>
                                        <div className="pg-field">
                                                <span className="pg-field__label">Last raced</span>
                                                <SegmentedControl
                                                        label="Last raced"
                                                        value={timeFrame}
                                                        onChange={setTimeFrame}
                                                        options={["All Time", "1 Year", "2 Years", "3 Years", "4 Years"].map((t) => ({ value: t, content: t === "All Time" ? "All time" : t.replace(" Years", "y").replace(" Year", "y") }))}
                                                />
                                        </div>
                                        <div className="pg-field">
                                                <span className="pg-field__label">Races</span>
                                                <SegmentedControl
                                                        label="Races"
                                                        value={startRange}
                                                        onChange={setStartRange}
                                                        options={["All", "5-50", "51-200", "201-500", "501+"].map((r) => ({ value: r, content: r === "All" ? "1+" : r }))}
                                                />
                                        </div>
                                        <div className="pg-field">
                                                <span className="pg-field__label">License</span>
                                                <SegmentedControl
                                                        label="License"
                                                        value={licenseClass}
                                                        onChange={setLicenseClass}
                                                        options={["All", "Rookie", "Class D", "Class C", "Class B", "Class A", "Pro"].map((l) => ({
                                                                value: l,
                                                                title: l,
                                                                content: l === "All" ? "All" : l === "Rookie" ? "R" : l.replace("Class ", ""),
                                                        }))}
                                                />
                                        </div>
                                </section>

                                <section className="hp-panel">
                                        <PanelHeader title={categoryTitle} />
                                        <p className="hp-panel__note">Drivers per 100-point iRating band, {filterSummary}.</p>
                                        {hasData && (
                                                <div className="pg-stats">
                                                        <div className="pg-stat">
                                                                <span className="pg-stat__label">Drivers</span>
                                                                <span className="pg-stat__value">{totalResults.toLocaleString()}</span>
                                                        </div>
                                                        <div className="pg-stat">
                                                                <span className="pg-stat__label">Mean</span>
                                                                <span className="pg-stat__value">{Math.round(mean).toLocaleString()}</span>
                                                        </div>
                                                        <div className="pg-stat">
                                                                <span className="pg-stat__label">Median</span>
                                                                <span className="pg-stat__value">{Math.round(median).toLocaleString()}</span>
                                                        </div>
                                                        <div className="pg-stat pg-stat--input">
                                                                <label className="pg-stat__label" htmlFor="userIRating">
                                                                        Your iRating
                                                                </label>
                                                                <input type="number" id="userIRating" name="userIRating" className="pg-input" value={userIRating} onChange={handleUserIRatingChange} placeholder="2150" />
                                                                {percentile !== null && <span className="pg-stat__result">Higher than {percentile.toFixed(1)}% of these drivers</span>}
                                                        </div>
                                                </div>
                                        )}
                                        <div className="hp-chart pg-chart-tall">
                                                {hasData ? (
                                                        <Plot data={data} layout={layout} config={{ displayModeBar: false }} />
                                                ) : !loading ? (
                                                        <p className="hp-panel__note">No drivers match these filters.</p>
                                                ) : null}
                                                {loading && (
                                                        <>
                                                                <LoadingOverlay />
                                                                <p className="pg-chart-wait">This can take up to 30 seconds.</p>
                                                        </>
                                                )}
                                        </div>
                                </section>

                                <p className="pg-fine">
                                        Time frames run up to today, so "1y" means drivers whose iRating last changed in an official race within the past year. Default ratings of 1350 and 1250 are left out.
                                </p>
                        </main>
                </div>
        );
};

export default IRatingGraph;
